// Hold-to-talk Deepgram wrapper for Firefly PTT Ask (§6).
// Independent from leads ASR and bug-report dictation; orchestrator
// calls start on arm and stop on release / hotkey keyup / 15s cap.

import { useCallback, useEffect, useRef } from "react";
import { useDeepgramHoldMic } from "@/lib/asr/useDeepgramHoldMic";
import type { DeepgramHoldMicStatus } from "@/lib/asr/deepgramHoldMicSession";
import { FIREFLY_PTT_MAX_DURATION_MS } from "@/lib/firefly/fireflyPttConstants";

export type FireflyPttMicStatus = DeepgramHoldMicStatus;

export type FireflyPttMicStopReason = "release" | "max_duration";

export type UseFireflyPttMicOptions = {
  onAppend: (chunk: string) => void;
  onStop?: (reason: FireflyPttMicStopReason) => void;
};

export type UseFireflyPttMicResult = {
  status: FireflyPttMicStatus;
  error: string | null;
  listening: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
};

export function useFireflyPttMic({
  onAppend,
  onStop,
}: UseFireflyPttMicOptions): UseFireflyPttMicResult {
  const onStopRef = useRef(onStop);
  useEffect(() => {
    onStopRef.current = onStop;
  }, [onStop]);

  const handleMaxDuration = useCallback(() => {
    onStopRef.current?.("max_duration");
  }, []);

  const { status, error, start: startMic, stop: stopMic } = useDeepgramHoldMic({
    onAppend,
    maxDurationMs: FIREFLY_PTT_MAX_DURATION_MS,
    onMaxDuration: handleMaxDuration,
  });

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const start = useCallback(async () => {
    await startMic();
  }, [startMic]);

  const stop = useCallback(async () => {
    const wasListening = statusRef.current === "listening";
    await stopMic();
    if (wasListening) onStopRef.current?.("release");
  }, [stopMic]);

  return {
    status,
    error,
    listening: status === "listening",
    start,
    stop,
  };
}
